/*
 * MockAgentTransport — deterministisches Scenario-Playback fuer die Delta-
 * Review-Entwicklung, bevor der echte Agent steht.
 *
 * Aktiviert ueber das Konsolen-Kommando /mock <name>. Kein Netzwerk, kein
 * LLM — das Scenario-Modul spielt eine feste Event-Sequenz ab. Reaktive
 * Scenarios koennen via `wait(state, toolCallId)` auf User-Entscheidungen
 * warten (A/R/T) und ihre weitere Ausfuehrung daran knuepfen.
 *
 * Siehe docs/archive/ai-agent-specification.md §11 Phase 0.5 (kommt mit der
 * Spec-Update in Phase D).
 */

import { AgentTransport } from './AgentTransport.js'
import { getScenario, listScenarios } from './scenarios/index.js'

export class MockAgentTransport extends AgentTransport {
    constructor({ delayScale = 1 } = {}) {
        super()
        // Skaliert alle emit()-Delays der Scenarios. Tests setzen 0 —
        // vorher summierten sich die Echtzeit-Sleeps der Scenario-Suite
        // auf ~40s pro Lauf (System-Review 2026-06-10, Kleinigkeit 21).
        this.delayScale = delayScale
        this._scenario = null                  // aktuell geladenes Scenario-Modul
        this._lastScenarioName = null          // fuer /mock repeat
        this._currentAbort = null              // AbortController des laufenden Runs
        this._state = null                     // State des laufenden Runs (fuer reportActionDone)
        this._currentProjectId = null
        // Marker fuer den ReviewController (Review v1.4 #2): Mock-Scenarios warten
        // auf Review-Verdicts via wait()/toolResults — der Controller MUSS sie
        // unmittelbar an den Transport melden. Real-Pfad puffert stattdessen via
        // _pendingResults und schickt die Verdicts im naechsten /api/agent/message-Body.
        this.reportsReviewVerdictsImmediately = true
        // Sidechannel-Hook fuer Scenarios, die externe Aenderungen am Modell
        // simulieren wollen (z.B. remote-change-during-review). main.js
        // verdrahtet das auf reviewController.onRemoteChange.
        this.onRemoteChange = null
    }

    /**
     * Sidechannel-API fuer Scenarios: simuliert eine Remote-Aenderung an
     * einem oder mehreren Paragraphen. Ruft den extern verdrahteten
     * onRemoteChange-Callback auf — typischerweise reviewController.onRemoteChange.
     * @param {Array<string|number>} paragraphIds
     */
    triggerRemoteChange(paragraphIds) {
        if (typeof this.onRemoteChange === 'function') {
            try {
                this.onRemoteChange(paragraphIds)
            } catch (err) {
                console.warn('[MockAgentTransport] onRemoteChange handler threw:', err)
            }
        }
    }

    /**
     * Laedt ein Scenario per Name. Nachfolgender sendMessage() spielt es ab.
     * @param {string} scenarioName
     * @returns {boolean} ob das Scenario gefunden wurde
     */
    loadScenario(scenarioName) {
        const scenario = getScenario(scenarioName)
        if (!scenario) return false
        this._scenario = scenario
        this._lastScenarioName = scenarioName
        return true
    }

    /** Deaktiviert den Mock — nachfolgende sendMessage-Calls sind no-op. */
    clearScenario() {
        this._scenario = null
    }

    /** @returns {string|null} Name des zuletzt geladenen Scenarios */
    getLastScenarioName() {
        return this._lastScenarioName
    }

    /** @returns {boolean} ob aktuell ein Scenario geladen ist */
    hasScenario() {
        return this._scenario !== null
    }

    /** @returns {Array<{name: string, description: string}>} */
    static listScenarios() {
        return listScenarios()
    }

    /**
     * Spielt das aktuell geladene Scenario ab.
     * @param {string} message - User-Input (wird vom Scenario ignorierbar)
     * @param {{ projectId?: string, cursorContext?: object }} context
     * @param {object} handlers - siehe AgentTransport
     */
    async sendMessage(message, context, handlers) {
        if (!this._scenario) {
            handlers?.onError?.(new Error('No mock scenario loaded. Use /mock <name> first.'))
            return
        }

        // Eventuell laufendes vorheriges Scenario abbrechen
        this.abort()

        const abortSig = new AbortController()
        this._currentAbort = abortSig

        const state = {
            handlers: handlers || {},
            context: { ...(context || {}), message },
            transport: this,
            abortSig,
            delayScale: this.delayScale,
            toolResults: new Map(),
            toolResolvers: new Map()
        }
        this._state = state

        try {
            await this._scenario.run(state)
        } catch (err) {
            if (err.name === 'AbortError') {
                handlers?.onAborted?.({ reason: 'user_requested' })
            } else {
                handlers?.onError?.(err)
            }
        } finally {
            if (this._state === state) {
                this._state = null
                this._currentAbort = null
            }
        }
    }

    /**
     * Empfaengt das Ergebnis einer propose_*-Aktion. Scenarios, die per
     * `wait(state, toolCallId)` warten, werden hier aufgeweckt.
     */
    async reportActionDone({ toolCallId, result, appliedVersion }) {
        if (!this._state) return
        const info = { result, appliedVersion }
        this._state.toolResults.set(toolCallId, info)
        const resolver = this._state.toolResolvers.get(toolCallId)
        if (resolver) {
            this._state.toolResolvers.delete(toolCallId)
            resolver.resolve(info)
        }
    }

    /** Bricht ein laufendes Scenario ab. setTimeouts werden via Signal gestoppt. */
    abort() {
        if (this._currentAbort) {
            this._currentAbort.abort()
            this._currentAbort = null
        }
    }

    async switchProject(projectId) {
        this._currentProjectId = projectId
    }

    destroy() {
        this.abort()
        this._scenario = null
        this._state = null
    }
}
